import React, { useEffect, useState } from "react";
import CreateDestination from "./CreateDestination";
import Swal from "sweetalert2";
import EditDestination from "./EditDestination";
import { getAllDestinations, deleteDestination } from "../../api/Destination";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import "../../vendor/css/viewdestination.css";

const ViewDestination = () => {
  const [createDestinationModalshow, setCreateDestinationModalshowShow] =
    useState(false);
  const handleCreateModalClose = () => setCreateDestinationModalshowShow(false);
  const handleCreateModalShow = () => setCreateDestinationModalshowShow(true);

  const [editDestinationModelShow, setEditDestinationModelShow] =
    useState(false);
  const handleEditModalClose = () => setEditDestinationModelShow(false);
  const handleEditModalShow = () => setEditDestinationModelShow(true);

  const [destinationList, setDestinationList] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const destinationsPerPage = 10;

  useEffect(() => {
    const getDestinationList = async () => {
      try {
        const response = await getAllDestinations();
        setDestinationList(response?.destinations || response || []);
      } catch (error) {
        console.error("Error fetching destinations:", error);
        Swal.fire("Error", "Failed to load destinations", "error");
      } finally {
        setLoading(false);
      }
    };
    getDestinationList();
    const interval = setInterval(getDestinationList, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Filter destinations based on search term
  const filteredDestinations = destinationList.filter(destination =>
    destination.destination_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const indexOfLastDestination = currentPage * destinationsPerPage;
  const indexOfFirstDestination = indexOfLastDestination - destinationsPerPage;
  const currentDestinations = filteredDestinations.slice(indexOfFirstDestination, indexOfLastDestination);
  const totalPages = Math.ceil(filteredDestinations.length / destinationsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleDeleteDestination = async (destinationToDelete) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteDestination(destinationToDelete)
          .then((res) => {
            if (res.status === 200) {
              Swal.fire("Deleted!", "Your file has been deleted.", "success");
              setDestinationList((prev) =>
                prev.filter((item) => item._id !== destinationToDelete._id)
              );
            }
          })
          .catch((err) => {
            console.error("Delete failed:", err);
            Swal.fire("Error", "Failed to delete destination", "error");
          });
      }
    });
  };

  if (loading) {
    return (
      <div className="view-destinations">
        <div className="destinations-header">
          <h1>Destinations Management</h1>
          <p>Manage and view all travel destinations</p>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading destinations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="view-destinations">
      <div className="destinations-header">
        <h1 style={{ fontFamily: 'serif' }}>Destinations Management</h1>
        <p style={{ fontFamily: 'serif' }}>Manage and view all travel destinations</p>
      </div>

      <div className="destinations-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <i className="bx bx-map"></i>
          </div>
          <div className="stat-content">
            <h3>{destinationList.length}</h3>
            <p>Total Destinations</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <i className="bx bx-search"></i>
          </div>
          <div className="stat-content">
            <h3>{filteredDestinations.length}</h3>
            <p>Filtered Results</p>
          </div>
        </div>
      </div>

      <div className="destinations-controls">
        <div className="search-container">
          <i className="bx bx-search search-icon"></i>
          <input
            type="text"
            placeholder="Search by destination name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <button
          className="btn btn-primary add-destination-btn"
          onClick={handleCreateModalShow}
        >
          <i className="bx bx-plus"></i>
          Add Destination
        </button>
      </div>

      <div className="destinations-grid">
        {currentDestinations.length > 0 ? (
          currentDestinations.map((destination, index) => (
            <div key={destination._id || index} className="destination-card">
              <div className="destination-info">
                <h3>{destination.destination_name}</h3>
                <div className="destination-actions">
                  <button
                    className="btn btn-primary edit-btn"
                    onClick={() => {
                      setSelectedDestination(destination);
                      handleEditModalShow();
                    }}
                    title="Edit Destination"
                  >
                    <i className="bx bx-edit"></i>
                    Edit
                  </button>
                  <button
                    className="btn btn-danger delete-btn"
                    onClick={() => handleDeleteDestination(destination)}
                    title="Delete Destination"
                  >
                    <i className="bx bx-trash"></i>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-data">
            <div className="no-data-content">
              <i className="bx bx-map-x"></i>
              <p>No destinations found</p>
              <small>Try adjusting your search criteria</small>
            </div>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            <i className="bx bx-chevron-left"></i>
          </button>

          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => paginate(i + 1)}
              className={`pagination-btn ${currentPage === i + 1 ? 'active' : ''}`}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            <i className="bx bx-chevron-right"></i>
          </button>
        </div>
      )}

      {/* Create Destination Modal */}
      <Modal
        show={createDestinationModalshow}
        onHide={handleCreateModalClose}
        className="destination-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Destination</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CreateDestination
            onSuccess={(res) => {
              console.log(res);
              setCreateDestinationModalshowShow(false);
              setDestinationList(res?.destinations || res);
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCreateModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Destination Modal */}
      <Modal
        show={editDestinationModelShow}
        onHide={handleEditModalClose}
        className="destination-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Destination</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EditDestination
            destination={selectedDestination}
            onSuccess={(res) => {
              setDestinationList(res?.destinations || res);
              setEditDestinationModelShow(false);
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleEditModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ViewDestination;
